import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import EmployeeCentral from './components/EmployeeCentral';
import EmployeeProfile from './components/EmployeeProfile';
import EmployeeAvailabilityManager from './components/EmployeeAvailabilityManager';
import ScheduleCreation from './components/ScheduleCreation';
import SettingsCentral from './components/SettingsCentral';
import TerminatedEmployees from './components/TerminatedEmployees';
import TimeClock from './components/TimeClock';
import TimeEntries from './components/EmployeeTimesheetNew';
import Payroll from './components/Payroll';
import Login from './components/Login';
import EmployeeDashboard from './components/employee/EmployeeDashboardSimple';
import AvailableShifts from './components/employee/AvailableShifts';
import TimeOffRequest from './components/employee/TimeOffRequest';
import EmployeeLogin from './components/employee/EmployeeLogin';
import ProtectedEmployeeRoute from './components/employee/ProtectedEmployeeRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ChangePassword from './components/employee/ChangePassword';
import SetupDemo from './components/SetupDemo';
import OnboardingRouter from './components/onboarding/OnboardingRouter';

export default function App() {
  const [mode, setMode] = useState("light"); // Default to light mode
  const [currentUser, setCurrentUser] = useState(null);

  // Function to load user-specific theme preference
  const loadUserTheme = () => {
    // Check for admin user
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        setCurrentUser({ type: 'admin', id: user.id });
        const userThemeKey = `themeMode_admin_${user.id}`;
        const savedMode = localStorage.getItem(userThemeKey) || "light";
        setMode(savedMode);
        return;
      } catch (err) {
        console.error('Error parsing admin user:', err);
      }
    }

    // Check for employee user
    const employeeUser = localStorage.getItem('loggedInEmployee');
    if (employeeUser) {
      try {
        const user = JSON.parse(employeeUser);
        setCurrentUser({ type: 'employee', id: user.id });
        const userThemeKey = `themeMode_employee_${user.id}`;
        const savedMode = localStorage.getItem(userThemeKey) || "light";
        setMode(savedMode);
        return;
      } catch (err) {
        console.error('Error parsing employee user:', err);
      }
    }

    // No user logged in, default to light mode
    setCurrentUser(null);
    setMode("light");
  };

  // Load user-specific theme preference
  useEffect(() => {
    loadUserTheme();

    // Listen for storage changes (when user logs in/out in different tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'adminUser' || e.key === 'loggedInEmployee') {
        loadUserTheme();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Expose theme reload function globally for login components to use
  useEffect(() => {
    window.reloadUserTheme = loadUserTheme;
    return () => {
      delete window.reloadUserTheme;
    };
  }, []);

  const theme = useMemo(
    () =>
      createTheme({
        palette: { mode },
        typography: {
          fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        },
      }),
    [mode]
  );

  // Create a dedicated dark theme for the employee portal
  const darkTheme = useMemo(() => createTheme({ palette: { mode: 'dark' } }), []);

  const handleToggle = () => {
    setMode((prev) => {
      const newMode = prev === "light" ? "dark" : "light";

      // Save theme preference for current user
      if (currentUser) {
        const userThemeKey = `themeMode_${currentUser.type}_${currentUser.id}`;
        localStorage.setItem(userThemeKey, newMode);
      }

      return newMode;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <DndProvider backend={HTML5Backend}>
          <Routes>
            {/* Authentication Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/employee/login" element={<EmployeeLogin />} />
            <Route
              path="/employee/change-password"
              element={
                <ProtectedEmployeeRoute requiresPasswordChange={true}>
                  {/* Employee portal forced dark mode */}
                  <ThemeProvider theme={darkTheme}>
                    <CssBaseline />
                    <ChangePassword />
                  </ThemeProvider>
                </ProtectedEmployeeRoute>
              }
            />
            <Route path="/setup-demo" element={<SetupDemo />} />

            {/* Employee Portal Routes (always dark mode) */}
            <Route
              path="/employee"
              element={
                <ProtectedEmployeeRoute>
                  <ThemeProvider theme={darkTheme}>
                    <CssBaseline />
                    <EmployeeDashboard />
                  </ThemeProvider>
                </ProtectedEmployeeRoute>
              }
            />
            <Route
              path="/employee/dashboard"
              element={
                <ProtectedEmployeeRoute>
                  <ThemeProvider theme={darkTheme}>
                    <CssBaseline />
                    <EmployeeDashboard />
                  </ThemeProvider>
                </ProtectedEmployeeRoute>
              }
            />
            <Route
              path="/employee/shifts"
              element={
                <ProtectedEmployeeRoute>
                  <ThemeProvider theme={darkTheme}>
                    <CssBaseline />
                    <AvailableShifts />
                  </ThemeProvider>
                </ProtectedEmployeeRoute>
              }
            />
            <Route
              path="/employee/time-off"
              element={
                <ProtectedEmployeeRoute>
                  <ThemeProvider theme={darkTheme}>
                    <CssBaseline />
                    <TimeOffRequest />
                  </ThemeProvider>
                </ProtectedEmployeeRoute>
              }
            />

            {/* Manager Portal Routes */}
            {/* ✅ Root should go to login, not dashboard */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            <Route
              path="/time-clock"
              element={
                <ProtectedRoute requiredFeature="time_clock_admin">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <TimeClock />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* ✅ Protect ALL manager pages */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredFeature="dashboard_admin">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-central"
              element={
                <ProtectedRoute requiredFeature="employee_central">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <EmployeeCentral />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/:employeeId"
              element={
                <ProtectedRoute requiredFeature="employee_central">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <EmployeeProfile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employees/terminated"
              element={
                <ProtectedRoute requiredFeature="employee_termination">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <TerminatedEmployees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-entries"
              element={
                <ProtectedRoute requiredFeature="time_entries">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <TimeEntries />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <ProtectedRoute requiredFeature="payroll">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <Payroll />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee-availability"
              element={
                <ProtectedRoute requiredFeature="employee_availability">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <EmployeeAvailabilityManager />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/schedule-creation"
              element={
                <ProtectedRoute requiredFeature="schedule_creation">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <ScheduleCreation />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Settings already protected; keep your feature gate */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredFeature="settings_central">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <SettingsCentral />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/onboarding/*"
              element={
                <ProtectedRoute requiredFeature="onboarding">
                  <Layout mode={mode} onToggleTheme={handleToggle}>
                    <OnboardingRouter />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* ✅ Catch-all should go to login (not dashboard) */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </DndProvider>
      </Router>
    </ThemeProvider>
  );
}
